import React, { Component } from "react";
import PropTypes from "prop-types";

import { ascending as d3Ascending } from "d3-array";
import {
  noop,
  strokeDashTypes,
  hexToRGBA,
  isDefined,
  getStrokeDasharray,
} from "../../utils";
import { saveNodeType, isHover } from "../utils";
import { getXValue } from "../../utils/ChartDataUtil";
import GenericChartComponent from "../../GenericChartComponent";
import { getMouseCanvas } from "../../GenericComponent";

import ClickableCircle from "../components/ClickableCircle";
import HoverTextNearMouse from "../components/HoverTextNearMouse";

class EachRectangle extends Component {
  constructor(props) {
    super(props);

    this.handleCorner1Drag = this.handleCorner1Drag.bind(this);
    this.handleCorner2Drag = this.handleCorner2Drag.bind(this);
    this.handleRectangleDragStart = this.handleRectangleDragStart.bind(this);
    this.handleRectangleDrag = this.handleRectangleDrag.bind(this);

    this.handleCorner1DragStart = this.handleCorner1DragStart.bind(this);
    this.handleCorner2DragStart = this.handleCorner2DragStart.bind(this);

    this.handleDragComplete = this.handleDragComplete.bind(this);

    this.handleHover = this.handleHover.bind(this);
    this.isHover = this.isHover.bind(this);
    this.renderSVG = this.renderSVG.bind(this);

    this.saveNodeType = saveNodeType.bind(this);
    this.nodes = {};

    this.state = {
      hover: false,
    };
  }

  isHover(moreProps) {
    const { x1Value, y1Value, x2Value, y2Value } = this.props;
    const {
      mouseXY,
      xScale,
      chartConfig: { yScale },
    } = moreProps;

    if (
      isDefined(x1Value) &&
      isDefined(y1Value) &&
      isDefined(x2Value) &&
      isDefined(y2Value)
    ) {
      const x1 = xScale(x1Value);
      const y1 = yScale(y1Value);
      const x2 = xScale(x2Value);
      const y2 = yScale(y2Value);

      const x = Math.min(x1, x2);
      const y = Math.min(y1, y2);
      const width = Math.abs(x2 - x1);
      const height = Math.abs(y2 - y1);

      const [mouseX, mouseY] = mouseXY;

      // Check if mouse is inside rectangle bounds
      return (
        mouseX >= x &&
        mouseX <= x + width &&
        mouseY >= y &&
        mouseY <= y + height
      );
    }
    return false;
  }

  handleRectangleDragStart() {
    const { x1Value, y1Value, x2Value, y2Value } = this.props;

    this.dragStart = {
      x1Value,
      y1Value,
      x2Value,
      y2Value,
    };
  }

  handleRectangleDrag(moreProps) {
    const { index, onDrag } = this.props;

    const { x1Value, y1Value, x2Value, y2Value } = this.dragStart;

    const {
      xScale,
      chartConfig: { yScale },
      xAccessor,
      fullData,
    } = moreProps;
    const { startPos, mouseXY } = moreProps;

    const x1 = xScale(x1Value);
    const y1 = yScale(y1Value);
    const x2 = xScale(x2Value);
    const y2 = yScale(y2Value);

    const dx = startPos[0] - mouseXY[0];
    const dy = startPos[1] - mouseXY[1];

    const newX1Value = getXValue(
      xScale,
      xAccessor,
      [x1 - dx, y1 - dy],
      fullData,
    );
    const newY1Value = yScale.invert(y1 - dy);
    const newX2Value = getXValue(
      xScale,
      xAccessor,
      [x2 - dx, y2 - dy],
      fullData,
    );
    const newY2Value = yScale.invert(y2 - dy);

    onDrag(index, {
      x1Value: newX1Value,
      y1Value: newY1Value,
      x2Value: newX2Value,
      y2Value: newY2Value,
    });
  }

  handleCorner1DragStart() {
    this.setState({
      anchor: "corner2",
    });
  }

  handleCorner2DragStart() {
    this.setState({
      anchor: "corner1",
    });
  }

  handleDragComplete(...rest) {
    this.setState({
      anchor: undefined,
    });
    this.props.onDragComplete(...rest);
  }

  handleCorner1Drag(moreProps) {
    const { index, onDrag } = this.props;
    const { x2Value, y2Value } = this.props;

    const [x1Value, y1Value] = getNewXY(moreProps);

    onDrag(index, {
      x1Value,
      y1Value,
      x2Value,
      y2Value,
    });
  }

  handleCorner2Drag(moreProps) {
    const { index, onDrag } = this.props;
    const { x1Value, y1Value } = this.props;

    const [x2Value, y2Value] = getNewXY(moreProps);

    onDrag(index, {
      x1Value,
      y1Value,
      x2Value,
      y2Value,
    });
  }

  handleHover(moreProps) {
    if (this.state.hover !== moreProps.hovering) {
      this.setState({
        hover: moreProps.hovering,
      });
    }
  }

  renderSVG(moreProps) {
    const { interactive, appearance } = this.props;
    const {
      x1Value,
      y1Value,
      x2Value,
      y2Value,
      r,
      edgeFill,
      edgeStroke,
      edgeInteractiveCursor,
      rectangleInteractiveCursor,
      hoverText,
      selected,
    } = this.props;

    // Extract appearance properties
    const stroke = appearance.stroke?.color || "#000000";
    const strokeWidth = appearance.stroke?.width || 1;
    const strokeOpacity = appearance.stroke?.opacity ?? 1;
    const strokeDash = appearance.stroke?.dash || "Solid";
    const fill = appearance.fill?.color || "#000000";
    const fillOpacity = appearance.fill?.opacity ?? 0.2;

    const { hover, anchor } = this.state;
    const {
      xScale,
      chartConfig: { yScale },
    } = moreProps;

    const x1 = xScale(x1Value);
    const y1 = yScale(y1Value);
    const x2 = xScale(x2Value);
    const y2 = yScale(y2Value);

    const x = Math.min(x1, x2);
    const y = Math.min(y1, y2);
    const width = Math.abs(x2 - x1);
    const height = Math.abs(y2 - y1);

    const lineStyle = {
      stroke: stroke,
      strokeWidth: hover || selected ? strokeWidth + 1 : strokeWidth,
      strokeOpacity: strokeOpacity,
      strokeDasharray: getStrokeDasharray(strokeDash),
    };

    const fillStyle = {
      fill: fill,
      fillOpacity: fillOpacity,
    };

    return (
      <g>
        {/* Fill rectangle */}
        <rect x={x} y={y} width={width} height={height} {...fillStyle} />

        {/* Border lines */}
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill="none"
          {...lineStyle}
        />

        {interactive !== false && (
          <>
            <ClickableCircle
              ref={this.saveNodeType("corner1")}
              show={selected || hover}
              cx={x1Value}
              cy={y1Value}
              r={r}
              fill={edgeFill}
              stroke={anchor === "corner1" ? stroke : edgeStroke}
              strokeWidth={
                edgeStroke && edgeStroke.width ? edgeStroke.width : 2
              }
              strokeOpacity={1}
              interactiveCursorClass={edgeInteractiveCursor}
              onDragStart={this.handleCorner1DragStart}
              onDrag={this.handleCorner1Drag}
              onDragComplete={this.handleDragComplete}
            />
            <ClickableCircle
              ref={this.saveNodeType("corner2")}
              show={selected || hover}
              cx={x2Value}
              cy={y2Value}
              r={r}
              fill={edgeFill}
              stroke={anchor === "corner2" ? stroke : edgeStroke}
              strokeWidth={
                edgeStroke && edgeStroke.width ? edgeStroke.width : 2
              }
              strokeOpacity={1}
              interactiveCursorClass={edgeInteractiveCursor}
              onDragStart={this.handleCorner2DragStart}
              onDrag={this.handleCorner2Drag}
              onDragComplete={this.handleDragComplete}
            />
          </>
        )}
      </g>
    );
  }

  render() {
    const { interactive } = this.props;
    const { selected, hoverText, rectangleInteractiveCursor } = this.props;

    const {
      enable: hoverTextEnabled,
      selectedText: hoverTextSelected,
      text: hoverTextUnselected,
      ...restHoverTextProps
    } = hoverText;

    const { hover } = this.state;

    return (
      <g>
        <GenericChartComponent
          isHover={this.isHover}
          onDragStart={
            interactive === false ? noop : this.handleRectangleDragStart
          }
          onDrag={interactive === false ? noop : this.handleRectangleDrag}
          onDragComplete={
            interactive === false ? noop : this.handleDragComplete
          }
          onHover={this.handleHover}
          onUnHover={this.handleHover}
          svgDraw={this.renderSVG}
          canvasDraw={null}
          canvasToDraw={getMouseCanvas}
          drawOn={["mousemove", "pan", "drag", "xAxisZoom"]}
          selected={selected}
          enableDragOnHover={interactive !== false}
          interactiveCursorClass={
            interactive === false ? undefined : rectangleInteractiveCursor
          }
        />
        <HoverTextNearMouse
          show={hoverTextEnabled && hover && interactive !== false}
          {...restHoverTextProps}
          text={selected ? hoverTextSelected : hoverTextUnselected}
        />
      </g>
    );
  }
}

export function getNewXY(moreProps) {
  const {
    xScale,
    chartConfig: { yScale },
    xAccessor,
    plotData,
    mouseXY,
  } = moreProps;
  const mouseY = mouseXY[1];

  const x = getXValue(xScale, xAccessor, mouseXY, plotData);

  const [small, big] = yScale.domain().slice().sort(d3Ascending);
  const y = yScale.invert(mouseY);
  const newY = Math.min(Math.max(y, small), big);

  return [x, newY];
}

EachRectangle.propTypes = {
  x1Value: PropTypes.any.isRequired,
  x2Value: PropTypes.any.isRequired,
  y1Value: PropTypes.any.isRequired,
  y2Value: PropTypes.any.isRequired,

  index: PropTypes.number,
  interactive: PropTypes.bool,

  appearance: PropTypes.shape({
    stroke: PropTypes.shape({
      color: PropTypes.string,
      width: PropTypes.number,
      opacity: PropTypes.number,
      dash: PropTypes.string,
    }),
    fill: PropTypes.shape({
      color: PropTypes.string,
      opacity: PropTypes.number,
    }),
  }).isRequired,

  r: PropTypes.number.isRequired,
  edgeFill: PropTypes.string.isRequired,
  edgeStroke: PropTypes.string.isRequired,
  edgeInteractiveCursor: PropTypes.string,
  rectangleInteractiveCursor: PropTypes.string,

  onDrag: PropTypes.func,
  onDragComplete: PropTypes.func,
  hoverText: PropTypes.object.isRequired,

  selected: PropTypes.bool,
};

EachRectangle.defaultProps = {
  onDrag: noop,
  onDragComplete: noop,

  edgeStroke: "#000000",
  edgeFill: "#FFFFFF",
  edgeStrokeWidth: 2,
  r: 5,
  edgeInteractiveCursor: "react-stockcharts-pointer-icon",
  rectangleInteractiveCursor: "react-stockcharts-move-cursor",
  hoverText: {
    enable: false,
  },
  interactive: true,
};

export default EachRectangle;
